import React from "react";
import PropTypes from "prop-types";
import { Spin } from "antd";
import { date } from "./data-utils";

/**
 * Presentational primitives shared by every Ops Workspace section.
 * Styling lives in styles/ops-workspace.css; nothing here holds state.
 */

export const Icon = ({ d, size = 17, stroke = "currentColor", width = 2 }) => (
    <svg
        width={size}
        height={size}
        viewBox={"0 0 24 24"}
        fill={"none"}
        stroke={stroke}
        strokeWidth={width}
        strokeLinecap={"round"}
        strokeLinejoin={"round"}
    >
        {String(d)
            .split("|")
            .map((p, i) => (
                <path key={i} d={p} />
            ))}
    </svg>
);

Icon.propTypes = {
    d: PropTypes.string.isRequired,
    size: PropTypes.number,
    stroke: PropTypes.string,
    width: PropTypes.number
};

export const ICONS = {
    check: "M20 6L9 17l-5-5",
    cross: "M18 6L6 18M6 6l12 12",
    search: "M21 21l-4.35-4.35|M19 11a8 8 0 1 1-16 0 8 8 0 0 1 16 0z",
    alert: "M12 7v6|M12 16.6v.4",
    chevron: "M9 18l6-6-6-6",
    arrow: "M5 12h14|M13 6l6 6-6 6",
    download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4|M7 10l5 5 5-5|M12 15V3",
    refresh:
        "M23 4v6h-6|M1 20v-6h6|M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15",
    mail: "M2 4h20v16H2z|M2 7l10 6 10-6",
    bolt: "M13 2L3 14h7l-1 8 10-12h-7z",
    eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z|M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z",
    pen: "M12 20h9|M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z",
    pin: "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z|M15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0z",
    bank: "M3 21h18|M5 21V9l7-5 7 5v12|M9 21v-6h6v6",
    person: "M16 20a4 4 0 0 0-8 0|M16 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0z",
    file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z|M14 2v6h6",
    idCard: "M2 5h20v14H2z|M10 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0z|M13 10h6M13 14h4",
    image: "M3 3h18v18H3z|M11 9a2 2 0 1 1-4 0 2 2 0 0 1 4 0z|M21 15l-5-5L5 21",
    clock: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z|M12 8v4l3 2",
    dash: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z|M8 12h8",
    checklist: "M9 11l3 3L22 4|M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
};

/**
 * Last line of defence before a value reaches React.
 *
 * These panels read straight off API payloads, where a field that looks
 * scalar is often a relation - a user, a bank branch, a list of charge slabs.
 * Handing React one of those throws (error #31) and unmounts the whole panel.
 * Rather than trust every call site, the value sinks coerce here: a person
 * becomes their name, a named object its name, a list a joined string.
 */
export const toDisplay = value => {
    if (value === null || value === undefined) return value;
    if (typeof value !== "object") return value;
    if (React.isValidElement(value)) return value;
    if (value.toJS) return toDisplay(value.toJS());
    if (Array.isArray(value)) {
        if (value.some(item => React.isValidElement(item))) return value;
        const parts = value
            .map(toDisplay)
            .filter(part => part !== null && part !== undefined && part !== "");
        return parts.length ? parts.join(", ") : null;
    }
    const label =
        value.full_name ||
        [value.first_name, value.middle_name, value.last_name]
            .filter(Boolean)
            .join(" ") ||
        value.name ||
        value.description ||
        value.label ||
        value.email;
    return label ? String(label) : null;
};

export const Pill = ({ tone = "neutral", children }) => (
    <span className={`ops-ws__pill ops-ws__pill--${tone}`}>{children}</span>
);

Pill.propTypes = {
    tone: PropTypes.oneOf(["neutral", "info", "ok", "warn", "bad"]),
    children: PropTypes.node
};

/** Filled circular status badge — the checklist's complete / wrong / missing. */
export const StatusBadge = ({ tone, small }) => {
    const path =
        tone === "ok" ? ICONS.check : tone === "bad" ? ICONS.cross : ICONS.alert;
    return (
        <span
            className={
                `ops-ws__badge ops-ws__badge--${tone}` +
                (small ? " ops-ws__badge--sm" : "")
            }
        >
            <Icon d={path} size={small ? 9 : 11} stroke={"#fff"} width={3.2} />
        </span>
    );
};

StatusBadge.propTypes = {
    tone: PropTypes.oneOf(["ok", "warn", "bad"]).isRequired,
    small: PropTypes.bool
};

export const Toggle = ({ on, onChange, disabled, label }) => (
    <div className={"ops-ws__toggle-row"}>
        {label ? <span className={"ops-ws__toggle-label"}>{label}</span> : null}
        <button
            type={"button"}
            className={"ops-ws__toggle" + (on ? " is-on" : "")}
            disabled={disabled}
            aria-pressed={!!on}
            aria-label={label || "toggle"}
            onClick={() => !disabled && onChange && onChange(!on)}
        >
            <span />
        </button>
    </div>
);

Toggle.propTypes = {
    on: PropTypes.bool,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    label: PropTypes.string
};

export const Card = ({ className = "", children }) => (
    <div className={`ops-ws__card ${className}`.trim()}>{children}</div>
);

Card.propTypes = { className: PropTypes.string, children: PropTypes.node };

export const KeyValue = ({ label, value, tone, span }) => {
    const shown = toDisplay(value);
    return (
        <div className={span ? "ops-ws__field ops-ws__field--span" : "ops-ws__field"}>
            <div className={"ops-ws__kv-label"}>{label}</div>
            <div
                className={
                    "ops-ws__kv-value" +
                    (tone === "missing"
                        ? " ops-ws__kv-value--missing"
                        : tone === "muted"
                            ? " ops-ws__kv-value--muted"
                            : "")
                }
            >
                {shown === null || shown === undefined || shown === "" ? "—" : shown}
            </div>
        </div>
    );
};

KeyValue.propTypes = {
    label: PropTypes.node,
    // Deliberately `any`: call sites hand this raw API values, and toDisplay
    // above is what turns a relation into something renderable.
    value: PropTypes.any,
    tone: PropTypes.oneOf(["missing", "muted"]),
    span: PropTypes.bool
};

/**
 * A labelled field. `accent` paints the left border — "ok" when the value has
 * been verified against a source of truth, "warn" when it is unverified or
 * mismatched. Read-only by default; pass onChange to make it editable.
 *
 * `required` marks a field the case cannot be completed without; it is starred
 * in the label and turns amber while it is empty, so a missing mandatory value
 * reads the same as any other gap on the workspace.
 *
 * A read-only date renders as text in the workspace's one date format rather
 * than as a disabled date input, because the input's format is the browser
 * locale's (`10/05/1975`) and put three different date formats on one panel.
 */
export const Field = ({
    label,
    value,
    accent,
    options,
    onChange,
    disabled,
    span,
    required,
    type = "text"
}) => {
    const editable = Boolean(onChange);
    const shown = toDisplay(value);
    const isEmpty = shown === null || shown === undefined || shown === "";
    const tone = accent || (required && isEmpty ? "warn" : undefined);
    const accentClass = tone ? ` ops-ws__input--${tone}` : "";
    // A read-only date is a display, not an editor: show it formatted.
    const val = isEmpty
        ? ""
        : type === "date" && !editable
            ? date(shown)
            : shown;
    return (
        <div
            className={span ? "ops-ws__field ops-ws__field--span" : "ops-ws__field"}
        >
            <div className={"ops-ws__field-label"} title={label}>
                {label}
                {required ? <span className={"ops-ws__req"}>{" *"}</span> : null}
            </div>
            {options ? (
                <select
                    className={`ops-ws__select${accentClass.replace(
                        "input",
                        "select"
                    )}`}
                    value={val}
                    disabled={disabled || !onChange}
                    onChange={e => onChange && onChange(e.target.value)}
                >
                    <option value={""}>—</option>
                    {options.map(o => (
                        <option key={String(o.value)} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            ) : (
                <input
                    className={`ops-ws__input${accentClass}`}
                    type={editable ? type : "text"}
                    value={val}
                    placeholder={label}
                    readOnly={!editable}
                    disabled={disabled}
                    onChange={e => onChange && onChange(e.target.value)}
                />
            )}
        </div>
    );
};

Field.propTypes = {
    label: PropTypes.string,
    value: PropTypes.any,
    accent: PropTypes.oneOf(["ok", "warn"]),
    options: PropTypes.array,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    span: PropTypes.bool,
    required: PropTypes.bool,
    type: PropTypes.string
};

/**
 * Card header — title, optional one-line note, optional right-hand slot.
 * Every panel uses this (rather than its own padding and font size) so card
 * titles sit on the same baseline down the whole workspace.
 */
export const CardHead = ({ title, sub, extra }) => (
    <div className={"ops-ws__card-head"}>
        <div>
            <div className={"ops-ws__card-head-title"}>{title}</div>
            {sub ? <div className={"ops-ws__card-head-sub"}>{sub}</div> : null}
        </div>
        {extra || null}
    </div>
);

CardHead.propTypes = {
    title: PropTypes.node,
    sub: PropTypes.node,
    extra: PropTypes.node
};

/** A headline figure in an action bar, with room for a control beside it. */
export const Metric = ({ label, value, children }) => (
    <div className={"ops-ws__metric"}>
        <div className={"ops-ws__metric-label"}>{label}</div>
        <div className={"ops-ws__metric-value"}>
            {value}
            {children}
        </div>
    </div>
);

Metric.propTypes = {
    label: PropTypes.node,
    value: PropTypes.node,
    children: PropTypes.node
};

export const SectionHeading = ({ title, subtitle, extra }) => (
    <div className={"ops-ws__section-head"}>
        <div>
            <div className={"ops-ws__h1"}>{title}</div>
            {subtitle ? <div className={"ops-ws__h1-sub"}>{subtitle}</div> : null}
        </div>
        {extra || null}
    </div>
);

SectionHeading.propTypes = {
    title: PropTypes.node,
    subtitle: PropTypes.node,
    extra: PropTypes.node
};

export const Segmented = ({ value, options, onChange, disabled }) => (
    <div className={"ops-ws__segmented" + (disabled ? " is-disabled" : "")}>
        {options.map(o => (
            <button
                key={o.value}
                type={"button"}
                className={value === o.value ? "is-active" : ""}
                disabled={disabled}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

Segmented.propTypes = {
    value: PropTypes.any,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

export const EmptyState = ({ children }) => (
    <div className={"ops-ws__empty"}>{children}</div>
);

EmptyState.propTypes = { children: PropTypes.node };

/**
 * Keeps one bad panel from taking the whole tab down.
 *
 * Handing React an object as a child (a charge slab list, say) throws during
 * render and unmounts the entire subtree — which reads to an operator as "the
 * tab went blank". This catches that, names the section, and leaves the rest
 * of the workspace usable.
 */
export class SectionErrorBoundary extends React.Component {
    state = { error: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error(`[ops-workspace] ${this.props.name} failed to render`, error, info);
    }

    componentDidUpdate(prevProps) {
        // A different section mounting clears the previous failure.
        if (prevProps.name !== this.props.name && this.state.error) {
            this.setState({ error: null });
        }
    }

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div className={"ops-ws__empty"}>
                <div style={{ fontWeight: 700, color: "#b42318", marginBottom: 6 }}>
                    {this.props.name} could not be displayed
                </div>
                <div style={{ fontSize: 12.5 }}>
                    {String(this.state.error && this.state.error.message)}
                </div>
                <div style={{ fontSize: 12, marginTop: 8 }}>
                    The rest of the workspace still works. Details are in the
                    browser console.
                </div>
            </div>
        );
    }
}

SectionErrorBoundary.propTypes = {
    name: PropTypes.string.isRequired,
    children: PropTypes.node
};

/**
 * Gate for an embedded screen (NACH Mandate, Agreement, Account/Facility).
 *
 * Those screens are written on the assumption that a parent already loaded the
 * case before mounting them — on their own tabs the tab strip sits inside a
 * LoaderSkeleton gated on the case-detail fetch. Several of their selectors
 * dereference props with no guard (AccountFacilityTab's `feeData` calls
 * `props.caseDetail.getIn(...)` outright), so mounting one a tick early throws
 * inside mapStateToProps and takes the panel down. Hold them until their data
 * is actually there.
 */
export const EmbeddedScreen = ({ ready, title, note, flush, children }) => (
    <div className={"ops-ws__embed" + (flush ? " ops-ws__embed--flush" : "")}>
        {title ? (
            <div className={"ops-ws__embed-head"}>
                <span className={"ops-ws__eyebrow ops-ws__eyebrow--primary"}>{title}</span>
                {note ? <span className={"ops-ws__embed-note"}>{note}</span> : null}
            </div>
        ) : null}
        {ready ? (
            children
        ) : (
            <div className={"ops-ws__spin"} style={{ minHeight: 180 }}>
                <Spin />
            </div>
        )}
    </div>
);

EmbeddedScreen.propTypes = {
    ready: PropTypes.bool,
    title: PropTypes.string,
    note: PropTypes.node,
    flush: PropTypes.bool,
    children: PropTypes.node
};
